import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { 
  ArrowLeftIcon,
  UserIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';

interface CreateBusinessUserRequest {
  fullName: string;
  email: string;
  phoneNumber?: string;
  type: string;
  status: string;
  businessUserRole: string;
}

interface Place {
  id: string;
  name: string;
  city: string;
  state: string;
}

const BusinessUserCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<CreateBusinessUserRequest>({
    fullName: '',
    email: '',
    phoneNumber: '',
    type: 'CoffeeShop',
    status: 'Pending',
    businessUserRole: 'CafeOwner'
  });

  // Buscar cafeterias disponíveis
  const { data: places, isLoading: isLoadingPlaces } = useQuery({
    queryKey: ['places'],
    queryFn: async () => {
      const response = await api.get('/admin/places');
      return response.data.places as Place[];
    }
  });

  // Mutação para criar usuário
  const createBusinessUserMutation = useMutation({
    mutationFn: async (data: CreateBusinessUserRequest) => {
      const response = await api.post('/admin/business-users', data);
      return response.data;
    },
    onSuccess: () => {
      navigate('/business-users');
    },
    onError: (error: any) => {
      console.error('Erro ao criar usuário:', error);
      alert('Erro ao criar usuário empresarial');
    }
  });

  const handleInputChange = (field: keyof CreateBusinessUserRequest, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createBusinessUserMutation.mutate(formData);
  };

  if (isLoadingPlaces) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/business-users')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Voltar para Usuários Empresariais
        </button>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Criar Usuário Empresarial</h1>
        <p className="text-gray-600">Adicione um novo usuário empresarial ao sistema</p>
      </div>

      {/* Formulário */}
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dados Básicos */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
              Dados Básicos
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo *
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Nome completo do usuário"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>
          </div>

          {/* Tipo e Status */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-green-600" />
              Tipo e Status
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Negócio *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="CoffeeShop">Cafeteria</option>
                  <option value="Corporate">Empresa</option>
                  <option value="Marketplace">Marketplace</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="Pending">Pendente</option>
                  <option value="Active">Ativo</option>
                  <option value="Inactive">Inativo</option>
                  <option value="Suspended">Suspenso</option>
                </select>
              </div>
            </div>
          </div>

          {/* Permissões */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <ShieldCheckIcon className="h-5 w-5 mr-2 text-purple-600" />
              Permissões
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função *
                </label>
                <select
                  required
                  value={formData.businessUserRole}
                  onChange={(e) => handleInputChange('businessUserRole', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="CafeOwner">Proprietário de Cafeteria</option>
                  <option value="SystemAdmin">Administrador do Sistema</option>
                  <option value="LegalAdmin">Administrador Legal</option>
                  <option value="SupportAdmin">Administrador de Suporte</option>
                </select>
              </div>
            </div>
          </div>

          {/* Cafeterias Disponíveis */}
          {places && places.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2 text-orange-600" />
                Cafeterias Disponíveis
              </h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  Cafeterias disponíveis para vinculação (será feito na edição):
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {places.map((place) => (
                    <div key={place.id} className="text-sm text-gray-700">
                      <span className="font-medium">{place.name}</span>
                      <span className="text-gray-500 ml-2">({place.city}, {place.state})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Botões */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/business-users')}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createBusinessUserMutation.isPending}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {createBusinessUserMutation.isPending ? 'Criando...' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BusinessUserCreatePage;
